import React, {useEffect, useMemo, useState} from "react";
import {collection, onSnapshot, orderBy, query} from "firebase/firestore";
import {db} from "../../Security/firebase-config";
import {useTeam} from "../../context/TeamContext";
import useTeamQuotaStatus from "../../hooks/useTeamQuotaStatus";
import {
  getLiveLobbyShutdownLabel,
  getLiveLobbyStatusLabel,
} from "../../utils/liveLobby";
import "./LiveLobbyPanel.css";

const ACTIVE_STATUSES = new Set(["starting", "live", "quota_shutdown_grace", "finalizing"]);

function toMillis(value) {
  if (!value) return null;
  if (typeof value?.toDate === "function") return value.toDate().getTime();
  if (typeof value?.seconds === "number") return value.seconds * 1000;
  if (typeof value === "number" && Number.isFinite(value)) return value;
  return null;
}

function formatDuration(ms) {
  if (!Number.isFinite(ms) || ms <= 0) return "0:00";
  const total = Math.max(0, Math.floor(ms / 1000));
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

export default function LiveLobbyPanel({
  mapId = null,
  title = "Live Now",
  onJoinLobby,
}) {
  const {teamId} = useTeam();
  const quotaStatus = useTeamQuotaStatus(teamId);
  const [lobbies, setLobbies] = useState([]);
  const [nowMs, setNowMs] = useState(() => Date.now());

  useEffect(() => {
    const timer = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!teamId) {
      setLobbies([]);
      return undefined;
    }

    const lobbyQuery = query(
      collection(db, "teams", teamId, "liveLobbies"),
      orderBy("startedAt", "desc"),
    );

    return onSnapshot(lobbyQuery, (snapshot) => {
      const next = snapshot.docs
        .map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }))
        .filter((lobby) => ACTIVE_STATUSES.has(String(lobby?.status || "")))
        .filter((lobby) => !mapId || lobby.mapId === mapId);

      setLobbies(next);
    });
  }, [mapId, teamId]);

  const visibleLobbies = useMemo(() => {
    return lobbies.map((lobby) => {
      const startedAtMs = toMillis(lobby.startedAt) || nowMs;
      const shutdownAtMs = toMillis(lobby.shutdownAt);
      const countdownSec =
        shutdownAtMs && shutdownAtMs > nowMs ?
          Math.max(0, Math.ceil((shutdownAtMs - nowMs) / 1000)) :
          0;

      return {
        ...lobby,
        startedAtMs,
        countdownSec,
      };
    });
  }, [lobbies, nowMs]);

  if (!visibleLobbies.length) return null;

  return (
    <section className="live-lobby-panel" aria-label="Active live lobbies">
      <div className="live-lobby-panel__head">
        <div>
          <p className="live-lobby-panel__eyebrow">Team Live Sessions</p>
          <h4>{title}</h4>
        </div>
        <span className="live-lobby-panel__count">
          {visibleLobbies.length} active
        </span>
      </div>

      <div className="live-lobby-panel__grid">
        {visibleLobbies.map((lobby) => {
          const statusJoinable = ["starting", "live", "quota_shutdown_grace"].includes(String(lobby?.status || ""));
          const joinBlocked = !quotaStatus.canWatchVod || !statusJoinable;
          const joinReason = quotaStatus.userEgressExceeded ?
            "Your monthly egress allowance has already been reached." :
            !statusJoinable ?
              "This live lobby is finalizing and can no longer be joined." :
            quotaStatus.uploadBlockMessage ||
              "You do not currently have access to join this live lobby.";

          return (
            <article className="live-lobby-card" key={lobby.id}>
              <div className="live-lobby-card__status">
                <span className="live-lobby-card__live-dot" />
                <span className="live-lobby-card__live-label">LIVE</span>
                <span className="live-lobby-card__pill">
                  {getLiveLobbyStatusLabel(lobby.status)}
                </span>
              </div>

              <h5>{lobby.title || "Live Lobby"}</h5>
              <p className="live-lobby-card__meta">
                {lobby.mapName || lobby.mapId || "Map"} • Host: {lobby.hostDisplayName || "Unknown"}
              </p>
              <p className="live-lobby-card__meta">
                Running {formatDuration(nowMs - lobby.startedAtMs)} • {Number(lobby.activeParticipantCount || 0)} participant(s)
              </p>

              {lobby.status === "quota_shutdown_grace" ? (
                <div className="live-lobby-card__warning">
                  <strong>{getLiveLobbyShutdownLabel(lobby.quotaShutdownReason)}</strong>
                  <span>
                    Lobby self-terminates in {lobby.countdownSec}s and those final 30 seconds will still be saved.
                  </span>
                </div>
              ) : null}

              <div className="live-lobby-card__actions">
                <button
                  type="button"
                  className="live-lobby-card__join"
                  onClick={() => onJoinLobby?.(lobby)}
                  disabled={joinBlocked}
                  title={joinBlocked ? joinReason : "Join Live Lobby"}
                >
                  Join Live Lobby
                </button>
                {joinBlocked ? (
                  <p className="live-lobby-card__hint">{joinReason}</p>
                ) : (
                  <p className="live-lobby-card__hint">
                    Join the live stream and shared notes for this match.
                  </p>
                )}
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}
